import { useState, useEffect } from "react";

import FormInput from "./FormInput";
import FormSelector from "./FormSelector";
import FormFileInput from "./FormFileInput";
import FormCheckbox from "./FormCheckbox";
import FormTextarea from "./FormTextarea";
import CustomColorSelector from "./CustomColorSelector";
import CustomSessionTypeSelector from "./CustomSessionTypeSelector";
import CustomClassTypeSelector from "./CustomClassTypeSelector";
import CustomPhysioSelector from "./CustomPhysioSelector";
import CustomVoucherSelector from "./CustomVoucherSelector";
import CustomPaymentMethodSelector from "./CustomPaymentMethodSelector";
import CustomPatientSelector from "./CustomPatientSelector";
import CustomAcquiredVoucherSelector from "./CustomAcquiredVoucherSelector";
import CustomWeeklyScheduleSelector from "./CustomWeeklyScheduleSelector";
import { classNames } from "../../../auxiliar/auxFunctions";

const CustomForm = ({ formConfig, errors, register, className = "", isLoading = false }) => {
    const [hasMounted, setHasMounted] = useState(false);

    useEffect(() => {
        // Add a small delay to ensure the component is fully rendered before enabling transitions
        const timer = setTimeout(() => setHasMounted(true), 100);
        return () => clearTimeout(timer);
    }, []);

    return (
        <div
            className={`${hasMounted ? "transition-all duration-300 ease-in-out" : ""} ${classNames(className)}`}
        >
            {Object.keys(formConfig).map((key) => {
                if (formConfig[key].condition) {
                    const type = formConfig[key].type;
                    const fieldWrapper = (component, className = "") => (
                        <div
                            key={key}
                            className={`${hasMounted ? "transition-opacity duration-300 ease-in-out" : ""} ${className}`}
                        >
                            {component}
                        </div>
                    );

                    if (
                        [
                            "text",
                            "number",
                            "password",
                            "email",
                            "date",
                            "datetime-local",
                            "time",
                        ].includes(type)
                    ) {
                        return fieldWrapper(
                            <FormInput
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                hasMounted={hasMounted}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "selector") {
                        return fieldWrapper(
                            <FormSelector
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                hasMounted={hasMounted}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "file") {
                        return fieldWrapper(
                            <FormFileInput
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                hasMounted={hasMounted}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "checkbox") {
                        return fieldWrapper(
                            <FormCheckbox
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                hasMounted={hasMounted}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "textarea") {
                        return fieldWrapper(
                            <FormTextarea
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                hasMounted={hasMounted}
                                isLoading={isLoading}
                            />,
                            "col-span-1 md:col-span-2",
                        );
                    } else if (type === "color_selector") {
                        return fieldWrapper(
                            <CustomColorSelector
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (
                        ["session_type_selector", "session_type_selector_with_metadata"].includes(
                            type,
                        )
                    ) {
                        return fieldWrapper(
                            <CustomSessionTypeSelector
                                metadata={type === "session_type_selector_with_metadata"}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (
                        [
                            "group_session_type_selector",
                            "group_session_type_selector_with_metadata",
                        ].includes(type)
                    ) {
                        return fieldWrapper(
                            <CustomClassTypeSelector
                                metadata={type === "group_session_type_selector_with_metadata"}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "physio_selector") {
                        return fieldWrapper(
                            <CustomPhysioSelector
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (
                        ["voucher_selector", "voucher_selector_with_metadata"].includes(type)
                    ) {
                        return fieldWrapper(
                            <CustomVoucherSelector
                                metadata={type === "voucher_selector_with_metadata"}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "acquired_voucher_selector") {
                        return fieldWrapper(
                            <CustomAcquiredVoucherSelector
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />,
                        );
                    } else if (type === "payment_method_selector") {
                        return (
                            <CustomPaymentMethodSelector
                                key={key}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors[formConfig[key].name]}
                                isLoading={isLoading}
                            />
                        );
                    } else if (type === "patient_selector") {
                        return (
                            <CustomPatientSelector
                                key={key}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors}
                                isLoading={isLoading}
                            />
                        );
                    } else if (type === "schedule") {
                        return (
                            <CustomWeeklyScheduleSelector
                                key={key}
                                customKey={key}
                                register={register}
                                config={formConfig[key]}
                                errors={errors}
                                isLoading={isLoading}
                            />
                        );
                    } else if (type === "title") {
                        return (
                            <h3 key={key} className={formConfig[key].classname}>
                                {formConfig[key].text}
                            </h3>
                        );
                    }

                    return <div key={key}></div>;
                }

                return null;
            })}
        </div>
    );
};

export default CustomForm;
